import { useMemo } from "react";
import { useTranslations } from "use-intl";

import type { FilterOption } from "@/components/ui/filter-select";
import { formatTranslatedCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { formatCountry, getFlagUrl } from "@garimpa/lib/countries";
import { FORMAT_CATEGORIES_WITH_OTHER } from "@garimpa/lib/formatGroups";

interface FacetCount {
  value: string;
  count: number;
}

interface ReleaseGroupFacets {
  formatCounts?: Record<string, number>;
  genreCounts?: FacetCount[];
  styleCounts?: FacetCount[];
  countryCounts?: FacetCount[];
  yearCounts?: FacetCount[];
  labelCounts?: FacetCount[];
  companyCounts?: FacetCount[];
}

function countOptions(facets: FacetCount[] | undefined): FilterOption[] {
  return (facets ?? [])
    .map((facet) => ({ value: facet.value, label: facet.value, count: facet.count }))
    .sort((a, b) => b.count - a.count);
}

function taxonomyOptions(
  facets: FacetCount[] | undefined,
  translator: Parameters<typeof formatTranslatedCatalogTaxonomyLabel>[1],
): FilterOption[] {
  return (facets ?? [])
    .map((facet) => ({
      value: facet.value,
      label: formatTranslatedCatalogTaxonomyLabel(facet.value, translator),
      count: facet.count,
    }))
    .sort((a, b) => b.count - a.count);
}

function countryOptions(facets: FacetCount[] | undefined, locale: string): FilterOption[] {
  return (facets ?? [])
    .map((facet) => ({
      value: facet.value,
      label: formatCountry(facet.value, locale, { includeUnknown: true }),
      count: facet.count,
      iconUrl: getFlagUrl(facet.value, 20) ?? undefined,
    }))
    .sort((a, b) => b.count - a.count);
}

function yearOptions(facets: FacetCount[] | undefined): FilterOption[] {
  return countOptions(facets).sort((a, b) => b.value.localeCompare(a.value));
}

function formatOptions(
  counts: Record<string, number> | undefined,
  label: (format: string) => string,
): FilterOption[] {
  return FORMAT_CATEGORIES_WITH_OTHER.filter((format) => (counts?.[format] ?? 0) > 0).map(
    (format) => ({ value: format, label: label(format), count: counts?.[format] ?? 0 }),
  );
}

export function useReleaseGroupFilterOptions(
  facets: ReleaseGroupFacets | null | undefined,
  locale: string,
) {
  const tReleaseFilters = useTranslations("shared.releaseFilters");
  const tGenres = useTranslations("shared.genres");
  const tStyles = useTranslations("shared.styles");

  return useMemo<Record<string, FilterOption[]>>(() => {
    return {
      format: formatOptions(facets?.formatCounts, (format) =>
        tReleaseFilters.has(format) ? tReleaseFilters(format) : format,
      ),
      genre: taxonomyOptions(facets?.genreCounts, tGenres),
      style: taxonomyOptions(facets?.styleCounts, tStyles),
      country: countryOptions(facets?.countryCounts, locale),
      year: yearOptions(facets?.yearCounts),
      label: countOptions(facets?.labelCounts),
      company: countOptions(facets?.companyCounts),
    };
  }, [facets, locale, tGenres, tReleaseFilters, tStyles]);
}
